import { environmentManager, type QueryFunctionContext, queryOptions } from '@tanstack/react-query';

import type { WagerId } from '../../../schema/wager/entry.js';
import { type IDefinition, InternalError } from '../../domain/entry.js';
import { type ExtractActionReturnType, wagerQueryKeys, type WagerWorkerMessageMap } from '../../lib/entry.js';
import type { getWager } from '../actions/wager/getWager.js';

type WagerData = ExtractActionReturnType<typeof getWager>;

type PostCalculateFn = (
  type: 'outcomeDistribution',
  payload: WagerWorkerMessageMap['outcomeDistribution']['payload']
) => Promise<WagerWorkerMessageMap['outcomeDistribution']['response']>;

const createFetcher =
  (post: PostCalculateFn, definition: IDefinition) =>
  ({
    queryKey: [{ wagerId, outcomeCount }],
    client,
  }: QueryFunctionContext<ReturnType<(typeof wagerQueryKeys)['outcomeDistribution']>>): Promise<
    WagerWorkerMessageMap['outcomeDistribution']['response']
  > => {
    const wager = client.getQueryData<WagerData>(wagerQueryKeys.wager(wagerId));

    if (!wager) {
      // TODO: replace with actual error class
      throw new InternalError(`No wager for: ${wagerId}. Please fetch it using useWager`);
    }

    const couponWithKey = {
      // key will ensure calculation of srow is cached in worker
      key: wagerId,
      ...wager.coupon,
    } satisfies WagerWorkerMessageMap['outcomeDistribution']['payload']['coupon'];

    return post('outcomeDistribution', { coupon: couponWithKey, outcomeCount, definition: definition.model });
  };

type QueryKeys = {
  wagerId: WagerId;
  outcomeCount: number;
};

type QueryFunctionDependencies = {
  post: PostCalculateFn;
  definition: IDefinition;
};

/**
 * Returns query options for fetching the expanded srows for a given wager.
 *
 * @param root0 Query key options
 * @param root0.wagerId The ID of the wager to calculate expanded srows for
 * @param root0.outcomeCount Number of outcomes for the sport/event type
 * @param root1 Query function dependencies
 * @param root1.post Function to post calculation requests
 * @param root1.definition The product definition model
 * @returns Query options for use with TanStack React Query
 */
export function outcomeDistributionOptions(
  { wagerId, outcomeCount }: QueryKeys,
  { post, definition }: QueryFunctionDependencies
): ReturnType<
  typeof queryOptions<
    WagerWorkerMessageMap['outcomeDistribution']['response'],
    Error,
    WagerWorkerMessageMap['outcomeDistribution']['response'],
    ReturnType<(typeof wagerQueryKeys)['outcomeDistribution']>
  >
> {
  return queryOptions<
    WagerWorkerMessageMap['outcomeDistribution']['response'],
    Error,
    WagerWorkerMessageMap['outcomeDistribution']['response'],
    ReturnType<(typeof wagerQueryKeys)['outcomeDistribution']>
  >({
    queryKey: wagerQueryKeys.outcomeDistribution(wagerId, outcomeCount),
    queryFn: createFetcher(post, definition),
    enabled: !environmentManager.isServer(),
  });
}
